import { useState, useCallback } from 'react';
import { FormField, ValidationErrors, UseFormValidationReturn } from './GenericFormTypes';

/**
 * Form Validation Hook
 * Provides comprehensive validation logic for form fields
 */

export const useFormValidation = <T>(
  data: T | Partial<T>,
  fields: FormField[]
): UseFormValidationReturn => {
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});

  // =============================================================================
  // Field-specific Validation Functions
  // =============================================================================

  const validateName = (value: string, fieldLabel: string): string | null => {
    if (!value || !value.trim()) {
      return null;
    }

    const nameRegex = /^[a-zA-ZçğıöşüÇĞIİÖŞÜ\s]+$/;
    if (!nameRegex.test(value.trim())) {
      return `${fieldLabel} sadece harf ve boşluk içerebilir`;
    }
    if (value.trim().length < 2) {
      return `${fieldLabel} en az 2 karakter olmalıdır`;
    }
    if (value.trim().length > 50) {
      return `${fieldLabel} en fazla 50 karakter olabilir`;
    }
    return null;
  };

  const validatePhone = (value: string): string | null => {
    if (!value || !value.trim()) {
      return null;
    }

    const phoneRegex = /^5[0-9]{9}$/;
    const cleanPhone = value.replace(/[^0-9]/g, '');
    if (!phoneRegex.test(cleanPhone)) {
      return 'Telefon numarası 5XX XXX XX XX formatında olmalıdır';
    }
    return null;
  };

  const validateTC = (value: string): string | null => {
    if (!value || !value.trim()) {
      return null;
    }

    const tcRegex = /^[1-9][0-9]{10}$/;
    if (!tcRegex.test(value)) {
      return 'TC Kimlik No 11 haneli olmalı ve 0 ile başlamamalıdır';
    }

    const digits = value.split('').map(Number);
    const odd = digits[0] + digits[2] + digits[4] + digits[6] + digits[8];
    const even = digits[1] + digits[3] + digits[5] + digits[7];
    const check1 = (odd * 7 - even) % 10;
    const check2 = (odd + even + digits[9]) % 10;

    if (check1 !== digits[9] || check2 !== digits[10]) {
      return 'Geçerli bir TC Kimlik Numarası giriniz';
    }
    return null;
  };

  const validateEmail = (value: string): string | null => {
    if (!value || !value.trim()) {
      return null;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value.trim())) {
      return 'Geçerli bir e-posta adresi giriniz';
    }
    return null;
  };

  // =============================================================================
  // Main Validation Function
  // =============================================================================

  const validateForm = useCallback((): boolean => {
    const errors: ValidationErrors = {};

    fields.forEach(field => {
      const value = (data as any)[field.key];
      const stringValue = value ? String(value).trim() : '';

      if (field.required && (!value || stringValue === '')) {
        errors[field.key] = `${field.label} alanı zorunludur`;
        return;
      }

      if (!stringValue) {
        return;
      }

      let validationError: string | null = null;

      switch (field.key) {
        case 'name':
        case 'surname':
          validationError = validateName(stringValue, field.label);
          break;
        case 'phone_number':
          validationError = validatePhone(stringValue);
          break;
        case 'identification_number':
          validationError = validateTC(stringValue);
          break;
        case 'email':
          validationError = validateEmail(stringValue);
          break;
      }

      if (validationError) {
        errors[field.key] = validationError;
      }
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [data, fields]);

  // =============================================================================
  // Error Management Functions
  // =============================================================================

  const clearValidationErrors = useCallback(() => {
    setValidationErrors({});
  }, []);

  const clearFieldError = useCallback((fieldKey: string) => {
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[fieldKey];
      return newErrors;
    });
  }, []);

  return {
    validationErrors,
    validateForm,
    clearValidationErrors,
    clearFieldError,
    setValidationErrors,
  };
};
